/**
 * /pep-prep: the paid landing page for the Meta campaign
 * "EduFocal Academy | Jamaica Lookalike | Signups | Test".
 *
 * Built to the campaign developer brief. Three things about it are deliberate:
 *
 * 1. The account is created HERE, in the page, rather than by bouncing the parent to /signup. The
 *    campaign optimises on CompleteRegistration, and every extra hop between the ad and the form
 *    is paid traffic that leaves.
 *
 * 2. It is honest about what is free. Creating an account is free but unlocks nothing: a new user's
 *    subscriptionStatus is "inactive" and ProtectedRoute sends them to the paywall. The genuinely
 *    free thing is the PEP Readiness Check, which needs no account at all. The brief requires the
 *    page to say whether the account or the assessment is free, so the page says exactly that, in
 *    the hero and again in the FAQ, rather than implying a free trial that does not exist.
 *
 * 3. On success it does NOT redirect to /subscribe. A parent who has just typed their details in
 *    should not have the next screen be a card form. They get a confirmation with one clear next
 *    action, the free readiness check, which is what the brief asks for and is also the only thing
 *    they can actually do without paying.
 *
 * Tracking: ViewContent on mount, SignupStart on the first real keystroke, and CompleteRegistration
 * only after the server confirms the account, carrying the server's event id so the browser Pixel
 * and the Conversions API event deduplicate. A resumed signup (an existing unpaid account whose
 * password matched) returns 200 without an event id and must NOT count as a registration.
 *
 * Every number and claim on this page is sourced. See marketing/meta-campaign/CREATIVE.md.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Illustration } from "@/components/Illustration";
import { resolveAttribution } from "@/lib/firstTouch";
import { metaViewContent, metaSignupStart, metaCompleteRegistration } from "@/lib/metaPixel";
import {
  CheckCircle2, ChevronDown, Trophy, Swords, Flame, BarChart3, Loader2, AlertCircle,
} from "lucide-react";

/** The Grade 6 sitting, from the pep_windows table on production. */
const PEP_DATES = [
  { date: "17 March 2027", what: "Language Arts" },
  { date: "18 March 2027", what: "Mathematics" },
  { date: "21 April 2027", what: "Science and Social Studies" },
  { date: "22 April 2027", what: "Ability Test" },
];

/** Verified against the production questions table: published, active, unarchived, grades 4 to 6. */
const QUESTION_COUNT = "18,013";

const SUBJECTS_BY_GRADE = [
  { grade: "Grade 4", subjects: "Language Arts, Mathematics, Science, Social Studies" },
  { grade: "Grade 5", subjects: "Language Arts, Mathematics, Science, Social Studies" },
  { grade: "Grade 6", subjects: "Language Arts, Mathematics, Science, Social Studies, Ability Test" },
];

const HOW_IT_WORKS = [
  {
    n: "1",
    title: "Create the account",
    body: "Free, and it takes about a minute. You only need your child's name, their grade and a phone number we can reach you on.",
  },
  {
    n: "2",
    title: "Take the free readiness check",
    body: "Ten PEP questions, marked straight away, with a report naming the topics to work on first. No payment, and you can take it before you decide anything.",
  },
  {
    n: "3",
    title: "Practise the topics that came back weak",
    body: "Every question earns XP toward the next level. Badges unlock as they go, and they can challenge a classmate to a ten question head to head.",
  },
];

const GAME_MECHANICS = [
  { Icon: Flame, title: "Streaks", body: "A day counts once they answer three or more questions. The streak is what gets them back tomorrow." },
  { Icon: Trophy, title: "XP, levels and badges", body: "Harder questions pay more XP, so there is no way to farm easy ones. Locked badges show a hint for how to get them." },
  { Icon: Swords, title: "Head to head duels", body: "Challenge a classmate to ten questions in a subject, both locked to the same question at the same time, with a rematch button." },
  { Icon: BarChart3, title: "A report you can read", body: "Marked as they go, with the topics to fix first named plainly instead of buried in a percentage." },
];

const FAQS = [
  {
    q: "Which grades is this for?",
    a: "Grades 4, 5 and 6. Those are the grades that sit PEP, and they are the grades the readiness check accepts. EduFocal Academy also covers Grade 7 and Grade 11, but this page is about PEP.",
  },
  {
    q: "Is it really free?",
    a: "Creating an account is free, and the PEP Readiness Check is free and does not even need an account. Full practice across every subject is a subscription: J$2,500 a month, or J$10,000 a year, for one grade. You can cancel any time and it stops at the end of the period you have paid for.",
  },
  {
    q: "Is there a free trial?",
    a: "Not by default. A trial only exists if you arrive with a promotional code that carries one, and those collect card details up front. If you want to see the quality of the questions before paying anything, take the readiness check instead. It is free and needs no card.",
  },
  {
    q: "What happens right after I register?",
    a: "You are signed in straight away and we point you at the free readiness check. Practice across all the subjects needs a subscription, so nothing is charged and nothing starts automatically.",
  },
  {
    q: "What subjects are covered?",
    a: "Language Arts, Mathematics, Science and Social Studies for Grades 4, 5 and 6, plus the Ability Test for Grade 6. There are " + QUESTION_COUNT + " published questions across those grades.",
  },
  {
    q: "When is PEP?",
    a: "Grade 6 sits Language Arts on 17 March 2027 and Mathematics on 18 March. Science and Social Studies follow on 21 April, and the Ability Test on 22 April.",
  },
  {
    q: "How do I get help?",
    a: "Email hello@edufocalgroup.com or use the contact form. We answer within 24 hours.",
  },
];

type FormState = "idle" | "submitting" | "success" | "error";

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  grade?: string;
  parentPhone?: string;
}

export default function PepPrep() {
  const [, setLocation] = useLocation();
  const [state, setState] = useState<FormState>("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const [existingAccount, setExistingAccount] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const startedRef = useRef(false);
  const errorRef = useRef<HTMLDivElement | null>(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    grade: "",
    parentPhone: "",
    marketingOptIn: true,
  });

  // Diagnostic only. The campaign optimises on CompleteRegistration, never on this.
  useEffect(() => {
    metaViewContent("PEP Prep Landing");
  }, []);

  /** Fires once, on the first real keystroke, not on focus. Focus is not intent. */
  function noteStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    metaSignupStart();
  }

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    noteStart();
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  }

  /**
   * Validate here rather than leaning on the server.
   *
   * firstName and lastName are `z.string()` with no minimum on the API, so an empty string is
   * accepted and creates a blank-name account. Email is not trimmed server-side either, so a
   * trailing space from a phone keyboard fails `.email()` and comes back as the generic
   * "Invalid registration data", which tells the parent nothing about which field is wrong.
   */
  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.firstName.trim()) e.firstName = "Please enter your child's first name";
    if (!form.lastName.trim()) e.lastName = "Please enter your child's last name";
    if (!form.email.trim()) e.email = "Please enter your email address";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "That email address does not look right";
    if (form.password.length < 8) e.password = "Use at least 8 characters";
    if (!form.grade) e.grade = "Choose your child's grade";
    const digits = form.parentPhone.replace(/\D/g, "");
    if (digits.length < 7) e.parentPhone = "Enter a phone number we can reach you on";
    return e;
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (state === "submitting") return;

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setFormError(null);
      return;
    }

    setState("submitting");
    setFormError(null);
    setExistingAccount(false);

    // Jamaica is +1. getFullPhone() in Signup.tsx sends dial code plus subscriber digits with no
    // leading "+", so 876 555 1234 goes up as 18765551234. Matched here so both entry points
    // store phone numbers in the same shape.
    const local = form.parentPhone.replace(/\D/g, "").replace(/^1/, "");
    const parentPhone = `1${local}`;

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          // Must be a number. The endpoint does not coerce, so "6" is a 400.
          grade: Number(form.grade),
          parentPhone,
          marketingOptIn: form.marketingOptIn,
          ...resolveAttribution(),
        }),
      });

      let data: any = null;
      try {
        data = await res.json();
      } catch {
        // A proxy error page or a truncated response. Fall through to the generic message.
      }

      if (!res.ok) {
        const message = data?.error || "Something went wrong creating your account. Please try again.";
        setExistingAccount(typeof message === "string" && message.includes("already exists"));
        setFormError(message);
        setState("error");
        return;
      }

      // An existing unpaid account whose password happened to match. The server logs them in and
      // returns 200 with resumed:true and NO event id. It is not a new registration and must not
      // be counted as one.
      if (data?.resumed) {
        setLocation("/dashboard");
        return;
      }

      metaCompleteRegistration(data?.registrationEventId);
      setState("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setFormError("We could not reach the server. Check your connection and try again.");
      setState("error");
    }
  }

  // Move focus to the error so a screen reader and a keyboard user both find out what happened.
  useEffect(() => {
    if (state === "error" && errorRef.current) errorRef.current.focus();
  }, [state]);

  if (state === "success") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="py-20 md:py-28 bg-gradient-to-br from-[#0789c3] via-[#0789c3] to-[#065f88]">
            <div className="container px-4 md:px-6 max-w-3xl mx-auto text-center text-white">
              <div className="flex justify-center mb-6">
                <Illustration name="owl-cheer" size={130} />
              </div>
              <h1 className="font-[Montserrat] text-3xl md:text-5xl font-bold tracking-tight mb-4">
                That's the account made
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-8">
                You're signed in. The best next step is the free readiness check: ten PEP questions,
                marked straight away, with a report naming the topics to work on first. Nothing is
                charged for it.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link href="/pep-check">
                  <Button
                    size="lg"
                    className="text-lg px-10 py-7 rounded-full bg-[#FFC209] text-gray-900 hover:bg-[#e6af08] shadow-xl min-h-14 font-[Montserrat] font-semibold w-full sm:w-auto"
                  >
                    Start the free readiness check
                  </Button>
                </Link>
                <Link href="/dashboard">
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-10 py-7 rounded-full border-white/70 bg-transparent text-white hover:bg-white/10 min-h-14 font-[Montserrat] font-semibold w-full sm:w-auto"
                  >
                    Go to the dashboard
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero. Everything the brief asks to be above the fold: what this is, who it is for, what
            it does, that the next step is an account, and what is actually free. */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0789c3] via-[#0789c3] to-[#065f88] py-16 md:py-24">
          <div className="container px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="text-white">
                <p className="font-[Montserrat] text-sm font-semibold uppercase tracking-widest text-white/70 mb-4">
                  PEP · Grades 4, 5 &amp; 6
                </p>
                <h1 className="font-[Montserrat] text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
                  PEP starts 17 March 2027
                </h1>
                <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6">
                  Grade 6 sits first, and it is closer than it feels. EduFocal Academy turns PEP
                  practice into XP, badges and quiz battles with classmates, so your child actually
                  does it.
                </p>
                <ul className="space-y-2.5 mb-8">
                  {[
                    `${QUESTION_COUNT} PEP questions across Grades 4, 5 and 6`,
                    "Marked as they go, with the weak topics named",
                    "Free readiness check, no card needed",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3 text-white/90">
                      <CheckCircle2 className="w-5 h-5 text-[#FFC209] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <a href="#signup" className="inline-block">
                  <Button
                    size="lg"
                    className="text-lg px-10 py-7 rounded-full bg-[#FFC209] text-gray-900 hover:bg-[#e6af08] shadow-xl min-h-14 font-[Montserrat] font-semibold"
                  >
                    Create your free account
                  </Button>
                </a>
                <p className="text-sm text-white/70 mt-4">
                  The account is free. Full practice is J$2,500 a month, cancel any time.
                </p>
              </div>

              {/* The form is in the hero on desktop so the primary action needs no scroll. On mobile
                  it moves below, where the anchor button leads. */}
              <div className="hidden lg:block">
                <SignupCard
                  form={form}
                  set={set}
                  onSubmit={onSubmit}
                  state={state}
                  formError={formError}
                  existingAccount={existingAccount}
                  fieldErrors={fieldErrors}
                  errorRef={errorRef}
                />
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                How it works
              </p>
              <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                Three steps, and the second one is free
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.n} className="rounded-xl border border-gray-200 p-6 bg-white">
                  <div className="w-11 h-11 rounded-xl bg-[#0789c3] text-white flex items-center justify-center font-[Montserrat] font-bold text-lg mb-4">
                    {s.n}
                  </div>
                  <h3 className="font-[Montserrat] font-semibold text-lg text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why they keep opening it */}
        <section className="py-16 md:py-20 bg-[#EBEFF4]/40">
          <div className="container px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                Why they keep opening it
              </p>
              <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                Practice that does not need nagging
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {GAME_MECHANICS.map(({ Icon, title, body }) => (
                <div key={title} className="rounded-xl border border-gray-200 bg-white p-6 flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#854099] text-white flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-[Montserrat] font-semibold text-lg text-gray-900 mb-1.5">{title}</h3>
                    <p className="text-gray-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PEP relevance: the grades, the subjects, the dates */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                  What is covered
                </p>
                <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-6">
                  Every PEP subject, by grade
                </h2>
                <div className="space-y-4">
                  {SUBJECTS_BY_GRADE.map((g) => (
                    <div key={g.grade} className="rounded-xl border border-gray-200 p-5">
                      <p className="font-[Montserrat] font-semibold text-gray-900 mb-1">{g.grade}</p>
                      <p className="text-gray-600">{g.subjects}</p>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 mt-5">
                  {QUESTION_COUNT} published questions across those three grades, plus interactive
                  lessons and PEP-style performance tasks marked with written feedback.
                </p>
              </div>
              <div>
                <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                  The dates
                </p>
                <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-6">
                  When Grade 6 sits PEP
                </h2>
                <ol className="space-y-3">
                  {PEP_DATES.map((d) => (
                    <li key={d.date} className="flex items-baseline gap-4 rounded-xl bg-[#EBEFF4]/60 px-5 py-4">
                      <span className="font-[Montserrat] font-bold text-[#854099] whitespace-nowrap">{d.date}</span>
                      <span className="text-gray-700">{d.what}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-gray-600 mt-5">
                  Grade 4 and Grade 5 sit later, and the work that gets them ready starts now.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust. Only the two credentials on the approved register, and the founding year. */}
        <section className="py-14 bg-[#854099]">
          <div className="container px-4 md:px-6 max-w-5xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-6 text-center text-white">
              <div>
                <p className="font-[Montserrat] font-bold text-lg mb-1">TIME</p>
                <p className="text-white/80 text-sm">Named the number one edtech company in the Caribbean</p>
              </div>
              <div>
                <p className="font-[Montserrat] font-bold text-lg mb-1">Ministry of Education</p>
                <p className="text-white/80 text-sm">Endorsed</p>
              </div>
              <div>
                <p className="font-[Montserrat] font-bold text-lg mb-1">Since 2011</p>
                <p className="text-white/80 text-sm">Teaching Jamaican students online</p>
              </div>
            </div>
          </div>
        </section>

        {/* Signup. On mobile this is the form; on desktop it repeats the hero card lower down. */}
        <section id="signup" className="py-16 md:py-20 bg-[#EBEFF4]/40 scroll-mt-20">
          <div className="container px-4 md:px-6 max-w-xl mx-auto">
            <div className="text-center mb-8">
              <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                Create your free account
              </p>
              <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                Start tonight
              </h2>
              <p className="text-gray-600 mt-3">
                Free to create. Nothing is charged, and the readiness check costs nothing either.
              </p>
            </div>
            <SignupCard
              form={form}
              set={set}
              onSubmit={onSubmit}
              state={state}
              formError={formError}
              existingAccount={existingAccount}
              fieldErrors={fieldErrors}
              errorRef={errorRef}
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="font-[Montserrat] font-semibold text-sm uppercase tracking-wider text-[#854099] mb-3">
                Questions
              </p>
              <h2 className="font-[Montserrat] text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                The things parents ask first
              </h2>
            </div>
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {FAQS.map((f) => (
                <FaqItem key={f.q} q={f.q} a={f.a} />
              ))}
            </div>
            <p className="text-center text-gray-600 mt-8">
              Still stuck? Email{" "}
              <a className="text-[#0789c3] underline" href="mailto:hello@edufocalgroup.com">
                hello@edufocalgroup.com
              </a>{" "}
              or use the{" "}
              <Link href="/contact" className="text-[#0789c3] underline">
                contact form
              </Link>
              . We answer within 24 hours.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left min-h-14"
      >
        <span className="font-[Montserrat] font-semibold text-gray-900">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && <p className="text-gray-600 leading-relaxed pb-5 -mt-1">{a}</p>}
    </div>
  );
}

interface CardProps {
  form: {
    firstName: string; lastName: string; email: string; password: string;
    grade: string; parentPhone: string; marketingOptIn: boolean;
  };
  set: <K extends keyof CardProps["form"]>(k: K, v: CardProps["form"][K]) => void;
  onSubmit: (e: React.FormEvent) => void;
  state: FormState;
  formError: string | null;
  existingAccount: boolean;
  fieldErrors: FieldErrors;
  errorRef: React.RefObject<HTMLDivElement | null>;
}

function SignupCard({ form, set, onSubmit, state, formError, existingAccount, fieldErrors, errorRef }: CardProps) {
  const busy = state === "submitting";
  const input =
    "w-full rounded-lg border border-gray-300 px-4 py-3 min-h-12 text-base text-gray-900 " +
    "focus:outline-none focus:ring-2 focus:ring-[#0789c3] focus:border-transparent disabled:bg-gray-50";
  const bad = "border-red-500 focus:ring-red-500";

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-2xl bg-white border border-gray-200 shadow-xl p-6 md:p-7">
      {formError && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="mb-5 rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-red-800 text-sm">{formError}</p>
            {existingAccount && (
              <Link href="/login" className="text-red-900 underline text-sm font-medium mt-1 inline-block">
                Log in instead
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="pp-first" label="Child's first name" error={fieldErrors.firstName}>
          <input
            id="pp-first" name="given-name" autoComplete="given-name" disabled={busy}
            className={`${input} ${fieldErrors.firstName ? bad : ""}`}
            value={form.firstName} onChange={(e) => set("firstName", e.target.value)}
          />
        </Field>
        <Field id="pp-last" label="Child's last name" error={fieldErrors.lastName}>
          <input
            id="pp-last" name="family-name" autoComplete="family-name" disabled={busy}
            className={`${input} ${fieldErrors.lastName ? bad : ""}`}
            value={form.lastName} onChange={(e) => set("lastName", e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field id="pp-grade" label="Grade" error={fieldErrors.grade}>
          <select
            id="pp-grade" disabled={busy}
            className={`${input} ${fieldErrors.grade ? bad : ""}`}
            value={form.grade} onChange={(e) => set("grade", e.target.value)}
          >
            <option value="">Choose a grade</option>
            <option value="4">Grade 4</option>
            <option value="5">Grade 5</option>
            <option value="6">Grade 6</option>
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <Field id="pp-email" label="Your email" error={fieldErrors.email}>
          <input
            id="pp-email" type="email" inputMode="email" autoComplete="email" disabled={busy}
            className={`${input} ${fieldErrors.email ? bad : ""}`}
            value={form.email} onChange={(e) => set("email", e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field id="pp-phone" label="Your phone number" error={fieldErrors.parentPhone} hint="Jamaica numbers only, e.g. 876 555 1234">
          <input
            id="pp-phone" type="tel" inputMode="tel" autoComplete="tel" disabled={busy}
            className={`${input} ${fieldErrors.parentPhone ? bad : ""}`}
            value={form.parentPhone} onChange={(e) => set("parentPhone", e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field id="pp-pass" label="Create a password" error={fieldErrors.password} hint="At least 8 characters">
          <input
            id="pp-pass" type="password" autoComplete="new-password" disabled={busy}
            className={`${input} ${fieldErrors.password ? bad : ""}`}
            value={form.password} onChange={(e) => set("password", e.target.value)}
          />
        </Field>
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input
          type="checkbox" disabled={busy} className="mt-1 w-4 h-4 accent-[#0789c3]"
          checked={form.marketingOptIn} onChange={(e) => set("marketingOptIn", e.target.checked)}
        />
        <span className="text-sm text-gray-600">
          Send me PEP tips and updates about my child's progress. You can unsubscribe any time.
        </span>
      </label>

      <Button
        type="submit" size="lg" disabled={busy}
        className="w-full mt-6 text-lg px-8 py-7 rounded-full bg-[#FFC209] text-gray-900 hover:bg-[#e6af08] shadow-lg min-h-14 font-[Montserrat] font-semibold disabled:opacity-70"
      >
        {busy ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            Creating your account
          </span>
        ) : (
          "Create your free account"
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center mt-4">
        Free to create. By continuing you agree to our{" "}
        <Link href="/terms-conditions" className="underline">Terms</Link> and{" "}
        <Link href="/privacy" className="underline">Privacy Policy</Link>.
      </p>
    </form>
  );
}

function Field({
  id, label, error, hint, children,
}: {
  id: string; label: string; error?: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {error && (
        <p className="text-xs text-red-600 mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
